/** Full column box in px, padding included. A tier `sum` is the width of every column visible at that tier. */

const SPARE = 16
const BAND = 8

export function widthSum(widths: object, keys: readonly string[]): number {
  const map = widths as Record<string, number>
  return keys.reduce((sum, key) => sum + map[key], 0)
}

export function tieredColumnsForWidth(
  shown: readonly string[],
  availablePx: number,
  base: readonly string[],
  tiers: readonly { sum: number; keys: readonly string[] }[],
): string[] {
  const have = new Set(shown)
  const keys = [...base]
  for (const tier of tiers) {
    const on = tier.keys.every((key) => have.has(key))
    const line = tier.sum + SPARE
    const keep = on ? availablePx >= line - BAND : availablePx > line + BAND
    if (keep) keys.push(...tier.keys)
  }
  return keys
}

export const ACTIVITY_WIDTH = { when: 136, action: 240, actor: 200, type: 120, target: 128 } as const
export const ACTIVITY_KEYS = ['when', 'action', 'actor', 'type', 'target'] as const

export const ACCOUNT_WIDTH = {
  login: 112,
  code: 128,
  type: 88,
  group: 176,
  lev: 88,
  status: 136,
  sync: 160,
  ib: 128,
} as const
export const ACCOUNT_BASE = ['login', 'code', 'type', 'group', 'lev', 'status', 'sync'] as const
const ACCOUNT_TIERS = [{ sum: 1016, keys: ['ib'] }]

export function accountColumnsForWidth(shown: readonly string[], availablePx: number): string[] {
  return tieredColumnsForWidth(shown, availablePx, ACCOUNT_BASE, ACCOUNT_TIERS)
}

export const BCA_WIDTH = { date: 112, amount: 120, desc: 240, acct: 128, matched: 144, imported: 136 } as const
export const BCA_KEYS = ['date', 'amount', 'desc', 'acct', 'matched', 'imported'] as const

export const EKYC_WIDTH = { when: 136, client: 200, code: 128, vendor: 120, status: 136, id: 128 } as const
export const EKYC_KEYS = ['when', 'client', 'code', 'vendor', 'status', 'id'] as const

export const ACCSUM_WIDTH = {
  login: 96,
  prev: 112,
  min: 120,
  mout: 128,
  comm: 128,
  int: 104,
  tpl: 144,
  newbal: 112,
  float: 120,
  equity: 104,
  lot: 120,
  dpl: 120,
  ib: 128,
  rate: 80,
} as const
export const ACCSUM_BASE = ['login', 'prev', 'min', 'mout', 'tpl', 'newbal', 'equity', 'dpl'] as const
const ACCSUM_TIERS = [
  { sum: 1056, keys: ['float'] },
  { sum: 1184, keys: ['comm'] },
  { sum: 1288, keys: ['int'] },
  { sum: 1408, keys: ['lot'] },
  { sum: 1536, keys: ['ib'] },
  { sum: 1616, keys: ['rate'] },
]

export function accSumColumnsForWidth(shown: readonly string[], availablePx: number): string[] {
  return tieredColumnsForWidth(shown, availablePx, ACCSUM_BASE, ACCSUM_TIERS)
}

export const AGREEMENT_WIDTH = {
  user: 144,
  name: 160,
  ib: 112,
  team: 112,
  email: 176,
  demo: 96,
  live: 96,
  kyc: 80,
  identity: 144,
  approved: 140,
  status: 184,
  risk: 88,
  fd: 136,
  created: 136,
  wa: 128,
  utms: 112,
  utmm: 112,
  utmc: 120,
} as const
export const AGREEMENT_BASE = ['name', 'email', 'status', 'created', 'live', 'demo', 'kyc'] as const
const AGREEMENT_TIERS = [
  { sum: 1016, keys: ['risk'] },
  { sum: 1160, keys: ['user'] },
  { sum: 1384, keys: ['ib', 'team'] },
  { sum: 1528, keys: ['identity'] },
  { sum: 1664, keys: ['fd'] },
  { sum: 1804, keys: ['approved'] },
  { sum: 1932, keys: ['wa'] },
  { sum: 2044, keys: ['utms'] },
  { sum: 2156, keys: ['utmm'] },
  { sum: 2276, keys: ['utmc'] },
]

export function agreementColumnsForWidth(shown: readonly string[], availablePx: number): string[] {
  return tieredColumnsForWidth(shown, availablePx, AGREEMENT_BASE, AGREEMENT_TIERS)
}
